import React, { ReactNode } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  ClientTokenContext,
  ClientTokenContextData,
  FeatureFlags,
  FeatureFlagsContext,
  useClientTokenManager,
  useFeatureFlags,
} from '@cko/dashboard-shared';
import { useThemes } from 'providers';
import { useDeveloperToolsEnabled } from '../../useDeveloperToolsEnabled';
import { useDeveloperToolsProvider } from '../../context';

export const OverridesProvider = ({ children }: { children: ReactNode }) => {
  const { overrides } = useDeveloperToolsProvider();
  const isDeveloperToolsEnabled = useDeveloperToolsEnabled();
  const { updateTheme } = useThemes();

  //feature flags set up
  const flags = useFlags();
  const featureFlagContext = useFeatureFlags();
  const overridenFlags: Record<string, any> = { ...flags, ...overrides.featureFlags };

  //TODO: need to support non boolean values
  const featureFlagOverrides: FeatureFlags = {
    getFeatureFlag: (key: string) => {
      return overridenFlags[key];
    },
    getMultiVariateFlag: (key: string) => {
      return overridenFlags[key];
    },
    isUserIdentified: featureFlagContext.isUserIdentified,
    setIsUserIdentified: featureFlagContext.setIsUserIdentified,
  };

  //Apply user settings override
  //Todo load original value once provided in user  preferences and add reset overide function

  if (overrides.userPreferences.theme) {
    updateTheme(overrides.userPreferences.theme);
  }

  //permissions set up
  const clientTokenContext: ClientTokenContextData = useClientTokenManager();
  const permissions = clientTokenContext.tokenProperties.permissions;
  const permissionsWithOverrides = {
    ...Object.fromEntries(permissions.map((permission) => [permission, true])),
    ...overrides.permissions,
  };
  const newPermissions = Object.entries(permissionsWithOverrides)
    .filter(([, value]) => value)
    .map(([key]) => key);

  const clonedTokenProperties = JSON.parse(JSON.stringify(clientTokenContext.tokenProperties));

  const overridenClientTokenContext: ClientTokenContextData = {
    ...clientTokenContext,
    tokenProperties: { ...clonedTokenProperties, permissions: newPermissions },
  };

  if (!isDeveloperToolsEnabled) {
    return <>{children}</>;
  }

  return (
    <FeatureFlagsContext.Provider value={featureFlagOverrides}>
      <ClientTokenContext.Provider value={overridenClientTokenContext}>
        {children}
      </ClientTokenContext.Provider>
    </FeatureFlagsContext.Provider>
  );
};
